import { collect } from 'collect.js';
import { SubscriptionProductType, TAX_BEHAVIOUR } from '../exports/Constants';
import { computeSubscriptionTax, roundHalfUp } from './PricingHelpers';
import TaxCalculator from './TaxCalculator';

/**
 * Annual-contract-discount calculations: clamping/validating the granted
 * percentage and applying it to subscription prices, tiers and basket line
 * items for display. Consumed by the subscription verification steps
 * (SubscriptionStep / SubscriptionWithTermStep), the discount input
 * (billing/AnnualContractDiscount) and the billing product views. The generic
 * pricing/tax/tier primitives (roundHalfUp, computeSubscriptionTax, …) stay in
 * PricingHelpers.js. See tests/services/helpers/ContractDiscountHelpers.test.js.
 */

/**
 * Annual-contract discount bounds. The percentage a manager may grant on an
 * annual commitment term is hard-capped to this range. `standardMax` is the cap
 * for most managers; `elevatedMax` is the higher cap reserved for admins and
 * sales leadership (see GrantElevatedAnnualContractDiscount). `max` is the
 * absolute cap — the highest value any role can grant — and is what read-time
 * clamps tolerate on already-persisted values.
 */
export const ANNUAL_CONTRACT_DISCOUNT = {
  min: 0,
  standardMax: 15,
  elevatedMax: 20,
  max: 20,
  default: 10,
};

/**
 * The maximum discount percentage the current manager may grant: their own cap
 * (15%, or 20% when they hold GrantElevatedAnnualContractDiscount) widened to
 * any discount already persisted on the contract. The widening lets a
 * lower-capped manager keep an elevated discount someone else granted without
 * being able to raise a new one past their own cap.
 */
export function maxContractDiscount(canGrantElevated = false, persistedDiscount = 0) {
  const cap = canGrantElevated ? ANNUAL_CONTRACT_DISCOUNT.elevatedMax : ANNUAL_CONTRACT_DISCOUNT.standardMax;

  return Math.max(cap, Number(persistedDiscount) || 0);
}

/**
 * Apply a contract discount percentage to a single amount, half-up rounded to
 * the cent. A falsy/zero percentage returns the amount untouched.
 */
export function applyContractDiscount(amount, percentage) {
  if (!percentage) {
    return amount;
  }

  return roundHalfUp(amount * (1 - percentage / 100));
}

/**
 * Discount a price's pricing tiers, always leaving the first tier (the base
 * fee, e.g. EUR 19.00) untouched. Returns a new tiers array.
 */
export function discountPriceTiers(tiers, percentage) {
  return collect(tiers ?? [])
    .map((tier, index) =>
      index === 0 || !percentage ? tier : { ...tier, amount: applyContractDiscount(tier.amount, percentage) },
    )
    .toArray();
}

/**
 * True for the base subscription plan types whose base/flat fee is never
 * discounted by the annual-contract discount — only their success-based usage
 * tiers (and standalone add-ons) are. The mobile-app plan is intentionally
 * excluded: it is a flat monthly fee that the annual discount applies to in
 * full (matching billing/MobileAppProduct), so it must not be treated as a
 * plan here.
 */
export function isPlanType(type) {
  return [
    SubscriptionProductType.Plan,
    SubscriptionProductType.LegacyPlan,
    SubscriptionProductType.GoogleAdsPlan,
  ].includes(type);
}

/**
 * Discount a price for display. Tiered prices keep their base fee (tier 0) and
 * discount the upper tiers. A flat amount is only discounted when
 * `discountFlatAmount` is true — the basic plan's flat fee must stay at list
 * price. Originals are never mutated — the persisted values stay undiscounted
 * so the backend can recompute the discount itself.
 */
export function discountPrice(price, percentage, discountFlatAmount = true) {
  if (!percentage) {
    return price;
  }

  if (price.tiers?.length > 0) {
    return { ...price, tiers: discountPriceTiers(price.tiers, percentage) };
  }

  if (!discountFlatAmount) {
    return price;
  }

  const amount = applyContractDiscount(price.amount, percentage);
  const { tax, taxed_amount } = computeSubscriptionTax(amount, price.tax_percentage, price.tax_behaviour);

  return { ...price, amount, tax: roundHalfUp(tax), taxed_amount: roundHalfUp(taxed_amount) };
}

/**
 * Clamp a discount percentage into the enforced annual-contract range. `max`
 * defaults to the absolute cap so read-time clamps tolerate any legitimately
 * persisted value; pass the current manager's cap (maxContractDiscount) when
 * clamping their own input.
 */
export function clampDiscount(percentage, max = ANNUAL_CONTRACT_DISCOUNT.max) {
  const value = percentage === null || percentage === undefined || percentage === '' ? NaN : Number(percentage);

  if (!Number.isFinite(value)) {
    return ANNUAL_CONTRACT_DISCOUNT.default;
  }

  return Math.min(max, Math.max(ANNUAL_CONTRACT_DISCOUNT.min, value));
}

/**
 * True when a discount percentage sits inside the enforced range. `max`
 * defaults to the absolute cap; pass the current manager's cap
 * (maxContractDiscount) when validating their own input.
 */
export function isDiscountInRange(percentage, max = ANNUAL_CONTRACT_DISCOUNT.max) {
  const value = percentage === null || percentage === undefined || percentage === '' ? NaN : Number(percentage);

  return Number.isFinite(value) && value >= ANNUAL_CONTRACT_DISCOUNT.min && value <= max;
}

/**
 * Discount a basket line item for display. Plans keep their base fee at list
 * price — tier 0 for the success-based scale, or the whole flat fee for a flat
 * basic plan — while their upper tiers discount; flat add-ons have
 * amount/tax/brutto recomputed against quantity.
 */
export function discountLineItem(lineItem, percentage) {
  if (!percentage) {
    return lineItem;
  }

  const { price } = lineItem;
  const isPlan = isPlanType(lineItem.product?.type ?? lineItem.type);

  if (price.tiers?.length > 0) {
    return { ...lineItem, price: discountPrice(price, percentage, !isPlan) };
  }

  // A flat plan fee (the basic plan price) is never discounted.
  if (isPlan) {
    return { ...lineItem, price: discountPrice(price, percentage, false) };
  }

  const calculator = TaxCalculator.make(lineItem.tax_behaviour);
  const amount = applyContractDiscount(lineItem.amount, percentage);

  return {
    ...lineItem,
    price: discountPrice(price, percentage, true),
    amount,
    tax: roundHalfUp(calculator.tax(amount, price.tax_percentage)),
    taxed_amount: roundHalfUp(calculator.brutto(amount, price.tax_percentage)),
  };
}

/**
 * True when the mobile-app plan is the subscription's main product — it
 * carries a mobile-app item and no base plan. A mobile-app plan sitting on top
 * of a regular plan is an add-on, not the main product.
 */
export function isMobileAppMainSubscription(subscription) {
  const items = collect(subscription?.items ?? []);

  return (
    !!items.first((item) => (item.product?.type ?? item.type) === SubscriptionProductType.MobileAppPlan) &&
    !items.first((item) => isPlanType(item.product?.type ?? item.type))
  );
}

/**
 * Discount a whole subscription for display: every line item goes through
 * discountLineItem and the amount/tax/taxed_amount totals are recomputed from
 * the discounted items (tax summing only exclusive-tax items, matching the
 * basket totals in SubscriptionStep). The original object is never mutated —
 * persisted values stay undiscounted so the backend recomputes the discount
 * itself.
 */
export function discountSubscription(subscription, percentage) {
  if (!percentage) {
    return subscription;
  }

  const items = collect(subscription?.items ?? [])
    .map((item) => discountLineItem(item, percentage))
    .toArray();

  return {
    ...subscription,
    items,
    amount: collect(items).reduce((carry, item) => carry + item.amount, 0),
    tax: collect(items)
      .where('tax_behaviour', TAX_BEHAVIOUR.exclusive)
      .reduce((carry, item) => carry + item.tax, 0),
    taxed_amount: collect(items).reduce((carry, item) => carry + item.taxed_amount, 0),
  };
}
